/**
 * @module Fiber Internet
 * @description
 */
import Head from 'next/head';

// Template
import Template from '@/templates/Default';

// Blocks
import HeroAddressCheck from '@/blocks/Heroes/HeroAddressCheck';
import BannerCTA from '@/blocks/Banners/BannerCTA';
import BannerList from '@/blocks/Banners/BannerList';
import ColumnContent from '@/blocks/ColumnContent';
import SplitSectionCopy from '@/blocks/SplitSection/SplitSectionCopy';
import FAQs from '@/blocks/FAQS/fiberInternetFAQS';

// Extras
import Tooltip from '@/blocks/Tooltip';

// Cohesion
import MonarchFallback from '@/blocks/MonarchFallback';
import { MonarchSlot } from '@redventures/cohesion-utils-react';

// Product Schemas
import { fiberProductSchemas } from '@/js/utils/schema';

/**
 * @function Page
 * @description
 */
const Page = () => (
    <>
      <Head>
        <title>Fiber Optic Internet | Internet Service Provider | Frontier</title>
        <meta name="description" content="Get the power of 100% fiber optic network for your home with fiber internet service from Frontier. Learn more about Frontier's high speed internet plans." />
        <link rel="canonical" href="https://frontier.com/shop/internet/fiber-internet" />
      </Head>

      <Template
        title="Get Fiber Internet Today"
        hero={
          <>
            { /* Fiber Internet | Above Hero */ }
            <MonarchSlot slotId="frdcFiberInternetAboveHero"/>

            { /* Fiber Internet | Hero */ }
            <MonarchSlot slotId="frdcFiberInternetHero"
            fallback={ <MonarchFallback height={ [ '88px', '96px', '96px' ] } /> }>
              <HeroAddressCheck
                headline={ <>Fiber is<br /><span className='u-color-blue'>the future</span></> }
                subHeadline={ <>Switch to Frontier<sup>&reg;</sup> Fiber Internet to be unstoppable online.</> }
                useSelectAreasLegal={ <>Frontier<sup>&reg;</sup> Fiber Internet available in select&nbsp;areas.</> }
              />
            </MonarchSlot>

            { /* Fiber Internet | Below Hero */ }
            <MonarchSlot slotId="frdcFiberInternetBelowHero"/>
          </>
        }
        primary={
          <>
            {/* Fiber Internet - Section 1 */}
            <MonarchSlot slotId="frdcFiberInternetSection1">
              <BannerList theme="dark" />
            </MonarchSlot>


            {/* Fiber Internet - Section 2 */}
            <MonarchSlot slotId="frdcFiberInternetSection2">
              <ColumnContent
                useTiles
                headline="What is Frontier Fiber Internet?"
                subhead="The Wi-Fi you’ve been waiting for"
                copy={ <>Work, live and play the way you want with Frontier<sup>&reg;</sup> Fiber.</> }
                items={ [
                  {
                    icon: 'cloud-upload-download',
                    headline: 'Super fast sharing',
                    content: 'Share as fast as you can stream on multiple devices at the same time.'
                  },
                  {
                    icon: 'global-wifi',
                    headline: 'Reliability? Check.',
                    content: 'Connect to what matters most with 99.9% network reliability. '
                  },
                  {
                    icon: 'speed-guage',
                    headline: 'Unlimited access',
                    content: <>No data caps, overage charges or hidden fees.<Tooltip disclosure="data-caps" /></>
                  }
                ] }
              />
            </MonarchSlot>

            {/* Fiber Internet - Section 3 */}
            <MonarchSlot slotId="frdcFiberInternetSection3">
              <SplitSectionCopy
                title='Get more rewards'
                subtitle='Join our 100% fiber-optic network to get the internet connection you deserve.'
                content={ [
                  {
                    copy: <>Frontier customers get $10/mo. off YouTube TV for one year.<span data-legal="youtubetv" /></>,
                    icon: true
                  },
                  {
                    copy: 'Get $10 off your monthly bill when you establish and maintain Auto Pay at checkout. Plus, you’ll never have to remember to pay your bill again.',
                    icon: true
                  },
                  {
                    copy: 'Premium Wi-Fi router included, up to a $599 value.',
                    icon: true
                  }
                ] }
                theme="dark"
                image={ {
                  url: '/pages/internet/girls-watching-movies-using-fiber-optic-internet.jpg',
                  blurUrl: '/pages/home/split-section-720x514.jpg',
                  alt: 'Woman Eating Popcorn'
                } }
                objectFit="contain"
              />
            </MonarchSlot>

            {/* Fiber Internet - Section 4 */}
            <MonarchSlot slotId="frdcFiberInternetSection4">
              <BannerCTA
                theme="blue"
                link={ { title: 'Shop now' } }
              />
            </MonarchSlot>

            {/* Fiber Internet - Section 5 */}
            <MonarchSlot slotId="frdcFiberInternetSection5">
              <FAQs />
            </MonarchSlot>

            { /* Fiber Internet | Sticky CTA */ }
            <MonarchSlot slotId="frdcFiberInternetStickyCta"/>
          </>
        }>
      </Template>
      { fiberProductSchemas().map( ( schema, index ) => (
        <script
          key={ `${ schema }${ index }` }
          type="application/ld+json"
          dangerouslySetInnerHTML={ {
            __html:
            `${ schema }`
          } }
        /> ) ) }
    </>
);

export default Page;
